<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { DataTable, InstantRangeInput, Expandable } from '@hantera/design-system'
import { useContext, useEvents, useLocalization } from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import { type DateRange, useJobsDashboardStore } from '@/stores/jobs/jobs-dashboard-store'
import { JobsDashboardDataProvider } from './data-provider'
import JobsLineChart from './JobsLineChart.vue'

const context = useContext()
const { jobs: jobsService } = useBackend()
const t = useLocalization('jobs')

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('viewTitle')
  }
})

// Create store with services
const store = useJobsDashboardStore({ jobsService })

// Create data provider that uses the store
const provider = new JobsDashboardDataProvider(store, t)

// Events subscription for live statistics updates
const events = useEvents()
let unsubscribe: (() => void) | null = null

function setupEventSubscription() {
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }

  if (events.available) {
    unsubscribe = events.subscribe({
      path: 'job-definitions',
      events: ['jobStatistics'],
      onEvent: (event) => {
        if (event.eventType === 'jobStatistics') {
          store.handleStatisticsEvent(event.data)
        }
      },
    })
  }
}

const dateRange = computed({
  get: () => store.dateRange,
  set: (value: DateRange) => store.setDateRange(value),
})

const selectedKeys = ref<Set<string>>(new Set())

function handleDefaultAction() {
  if (selectedKeys.value.size > 0) {
    const key = Array.from(selectedKeys.value)[0]
    context.openView(`/jobs/definition?id=${encodeURIComponent(key)}`, {})
  }
}

// Refresh on date range change
watch(
  () => store.dateRange,
  () => {
    store.refresh()
  },
)

let refreshInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  store.refresh()
  setupEventSubscription()
  refreshInterval = setInterval(() => {
    store.refresh()
  }, 60_000)
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
  if (unsubscribe) {
    unsubscribe()
    unsubscribe = null
  }
})

const displayCharts = ref(true)
</script>

<template>
  <div class="jobs-dashboard-view">
    <div class="toolbar">
      <div class="date-filter">
        <label>{{ t('filters.dateRange') }}</label>
        <InstantRangeInput v-model="dateRange" />
      </div>
    </div>

      <Expandable label="Charts" class="charts-section" v-model="displayCharts">
        <div class="charts-container">
          <div class="chart-wrapper box-content">
            <JobsLineChart
              :title="t('charts.completedTitle')"
              :labels="store.timeLabels"
              :datasets="store.completedLineChartDatasets"
            />
          </div>
          <div class="chart-wrapper box-content">
            <JobsLineChart
              :title="t('charts.executionTimeTitle')"
              :labels="store.timeLabels"
              :datasets="store.executionTimeLineChartDatasets"
            />
          </div>
        </div>
      </Expandable>
      

    <DataTable
      :provider="provider"
      cache-key="jobs-dashboard"
      v-model:selected="selectedKeys"
      @defaultAction="handleDefaultAction"
    />
  </div>
</template>

<style scoped>
.jobs-dashboard-view {
  height: 100%;
  display: grid;
  grid-template-rows: min-content max-content auto;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-filter label {
  font-size: 14px;
  color: hsl(var(--color-40));
}

.charts-section {

  flex-shrink: 0;
  padding: 1rem;
}

.charts-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  min-height: 250px;
  max-height: 300px;
}

.chart-wrapper {
  padding: 1rem;
  height: 100%;
}

.table-section {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
